import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getDayOrderExpireAt, parseJSON } from '../../../../helpers/commonHelper';
import { toastMsg } from '../../../../components/common/Toast/Toast';
import {
  buildTradeOrderBody,
  enrichWalletFromLinkedSiblings,
  getLinkedWalletAddress,
  getPaymentAssetLabel,
  isNonCustodialWallet,
} from '../../../../services/wallet/linkedWallet.utils';
import { ATS } from '../../../../features/ats/atsSlice';
import { refreshActivePairBalance } from '../../../../services/wallet/tradeBalanceRefresh.service';
import {
  ensureDvpTradeAllowance,
  getDvpOrderBlockReason,
  signDvpOrderForTrade,
} from '../../../../services/wallet/nonCustodialDvpOrder.service';
import { logNonCustodialBalance } from '../../../../services/wallet/nonCustodialBalance.debug';
import { getNonCustodialWeb3Session, setNonCustodialRpcBusy } from '../../../../services/wallet/nonCustodialSession';
import { buildAllowanceApprovalToastMessage } from '../../../../services/wallet/allowanceApprovalMessaging';
import { PLACE_ORDER_EXECUTION, PLACE_ORDER_MARKET_MODE } from '../placeOrder.constants';
import {
  extractApiErrorMessage,
  isApiSuccessResponse,
  isRecoverablePlaceOrderTimeout,
  rowMatchesSubmittedOrder,
  PLACE_ORDER_TIMEOUT_MESSAGE,
} from '../../../../services/order/orderApi.utils';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const collectListRows = (response) => {
  const payload = response?.data ?? response;
  const lists = [
    payload?.orderlist,
    payload?.orderList,
    payload?.tradeList,
    payload?.tradelist,
    payload?.data?.orderlist,
    payload?.data?.tradeList,
  ];
  for (const list of lists) {
    if (Array.isArray(list)) return list;
  }
  return [];
};

/** After 504, BitGo often finishes place-order a few seconds later. */
async function confirmOrderAfterGatewayTimeout({ submitted, pairName }) {
  const pair = pairName || submitted?.pairName;
  if (!pair) return false;

  for (let attempt = 0; attempt < 8; attempt += 1) {
    await sleep(attempt === 0 ? 1500 : 2000);
    const [openOrders, history, trades] = await Promise.all([
      ATS.fetchUserOrderList({ page: 1, limit: 10, pairName: pair, status: 1 }),
      ATS.fetchUserOrderHistory({ page: 1, limit: 10, pairName: pair }),
      ATS.getUserTrades({ offset: 0, limit: 10, pairName: pair }),
    ]);
    const rows = [
      ...collectListRows(openOrders),
      ...collectListRows(history),
      ...collectListRows(trades),
    ];
    if (rows.some((row) => rowMatchesSubmittedOrder(row, submitted))) {
      return true;
    }
  }
  return false;
}

export function usePlaceOrderSubmission({
  formikRef,
  selectedTradeWallet,
  linkedWallets,
  selectedPair,
  selectedSide,
  setIsSubmitted,
  setShowModal,
  setTradeAmountResetKey,
  setIsApprovingAllowance,
  setApprovingAssetLabel,
  allowanceInFlightRef,
  setCreateOrderApiError,
  getGTDExpiryTime,
}) {
  const dispatch = useDispatch();

  const resolveSubmissionWallet = useCallback(
    () =>
      selectedTradeWallet
        ? enrichWalletFromLinkedSiblings(selectedTradeWallet, linkedWallets)
        : null,
    [linkedWallets, selectedTradeWallet],
  );

  const placeOrder = useCallback(
    async (request) => {
      const formik = formikRef.current;
      const body = parseJSON(request);
      setCreateOrderApiError('');
      const tradeWallet = resolveSubmissionWallet();
      if (!tradeWallet?.walletId) {
        toastMsg.error('Please select a wallet before placing an order.');
        setIsSubmitted(false);
        setShowModal(false);
        return;
      }

      const isPerpetualMarket =
        body?.marketMode === PLACE_ORDER_MARKET_MODE.PERPETUAL &&
        body?.executionType !== PLACE_ORDER_EXECUTION.LIMIT;

      if (isPerpetualMarket) {
        setIsSubmitted(false);
        setShowModal(false);
        toastMsg.info('Perpetual market order placement is not available yet.');
        return;
      }

      delete body?.pair_key;
      delete body?.marketMode;
      delete body?.executionType;
      delete body?.marginMode;
      delete body?.leverage;
      delete body?.tpSl;
      delete body?.takeProfit;
      delete body?.stopLoss;
      delete body?.reduceOnly;
      if (body.advanceOrderType === 'GTD') body.expire_at = getGTDExpiryTime();
      if (body.advanceOrderType === 'DAY') body.expire_at = getDayOrderExpireAt();

      const nonCustodialWeb3 = getNonCustodialWeb3Session();
      const isNonCustodial =
        isNonCustodialWallet(tradeWallet) && nonCustodialWeb3;

      setIsSubmitted(true);

      try {
        if (isNonCustodial && !body?.dvpOrder) {
          const dvpBlockReason = getDvpOrderBlockReason({
            wallet: tradeWallet,
            selectedPair,
          });
          if (dvpBlockReason) {
            logNonCustodialBalance('placeOrder:dvp-blocked', {
              reason: dvpBlockReason,
              assetTokenAddress: tradeWallet?.assetTokenAddress,
              tokenContracts: tradeWallet?.tokenContracts ?? {},
              settlement: tradeWallet?.settlement ?? null,
            });
            toastMsg.error(dvpBlockReason);
            setIsSubmitted(false);
            return;
          }

          const approvalAssetLabel =
            getPaymentAssetLabel(selectedPair, body.side ?? selectedSide) || 'TOKEN';

          await ensureDvpTradeAllowance({
            web3: nonCustodialWeb3,
            wallet: tradeWallet,
            selectedPair,
            quantity: body.quantity,
            price: body.price,
            side: body.side,
            ownerAddress: getLinkedWalletAddress(tradeWallet),
            onApprovalTxRequested: (approvalCtx) => {
              const symbol =
                approvalCtx?.assetSymbol || approvalAssetLabel || 'TOKEN';
              setApprovingAssetLabel(symbol);
              setIsApprovingAllowance(true);
              toastMsg.info(
                buildAllowanceApprovalToastMessage({
                  ...approvalCtx,
                  assetSymbol: symbol,
                  mode: approvalCtx?.mode || 'approve',
                }),
              );
            },
          });

          setIsApprovingAllowance(false);
          setApprovingAssetLabel('');

          setNonCustodialRpcBusy(true);
          try {
            logNonCustodialBalance('placeOrder:sign-dvp-start', {
              ownerAddress: getLinkedWalletAddress(tradeWallet),
            });
            toastMsg.info('MetaMask will ask you to sign this order to place it on the exchange.');
            body.dvpOrder = await signDvpOrderForTrade({
              web3: nonCustodialWeb3,
              wallet: tradeWallet,
              selectedPair,
              ownerAddress: getLinkedWalletAddress(tradeWallet),
              quantity: body.quantity,
              price: body.price,
              side: body.side,
              advanceOrderType: body.advanceOrderType,
              getGTDExpiryTime,
            });
          } finally {
            setNonCustodialRpcBusy(false);
          }
        }

        const orderPayload = buildTradeOrderBody(body, tradeWallet);
        const response = await dispatch(ATS.placeOrder(orderPayload));

        const finishPlaced = () => {
          formik?.resetForm();
          setShowModal(false);
          setCreateOrderApiError('');
          setIsSubmitted(false);
          setTradeAmountResetKey((key) => key + 1);

          window.dispatchEvent(
            new CustomEvent('open-orders:refresh', {
              detail: { pairName: body?.pairName },
            }),
          );

          if (selectedPair?.baseCurrency && selectedPair?.otherCurrency) {
            refreshActivePairBalance(
              dispatch,
              { selectedPair, selectedTradeWallet: tradeWallet },
              { coalesce: true },
            );
          }
        };

        if (isApiSuccessResponse(response)) {
          finishPlaced();
        } else if (isRecoverablePlaceOrderTimeout(response)) {
          const placed = await confirmOrderAfterGatewayTimeout({
            submitted: body,
            pairName: body?.pairName,
          });
          if (placed) {
            toastMsg.success('Order placed successfully.');
            finishPlaced();
          } else {
            setCreateOrderApiError(PLACE_ORDER_TIMEOUT_MESSAGE);
            setIsSubmitted(false);
            window.dispatchEvent(
              new CustomEvent('open-orders:refresh', {
                detail: { pairName: body?.pairName },
              }),
            );
            return;
          }
        } else {
          const errorMessage = extractApiErrorMessage(response);
          setCreateOrderApiError(errorMessage);
          setIsSubmitted(false);
          return;
        }
      } catch (error) {
        if (isRecoverablePlaceOrderTimeout(error)) {
          const placed = await confirmOrderAfterGatewayTimeout({
            submitted: body,
            pairName: body?.pairName,
          });
          if (placed) {
            toastMsg.success('Order placed successfully.');
            formik?.resetForm();
            setShowModal(false);
            setCreateOrderApiError('');
            setIsSubmitted(false);
            setTradeAmountResetKey((key) => key + 1);
            window.dispatchEvent(
              new CustomEvent('open-orders:refresh', {
                detail: { pairName: body?.pairName },
              }),
            );
            if (selectedPair?.baseCurrency && selectedPair?.otherCurrency) {
              refreshActivePairBalance(
                dispatch,
                { selectedPair, selectedTradeWallet: tradeWallet },
                { coalesce: true },
              );
            }
          } else {
            setCreateOrderApiError(PLACE_ORDER_TIMEOUT_MESSAGE);
            setIsSubmitted(false);
          }
          return;
        }
        setCreateOrderApiError(error?.message || 'Failed to place order.');
        setIsSubmitted(false);
        return;
      } finally {
        setIsApprovingAllowance(false);
        setApprovingAssetLabel('');
      }
    },
    [
      dispatch,
      formikRef,
      getGTDExpiryTime,
      linkedWallets,
      resolveSubmissionWallet,
      selectedPair,
      selectedSide,
      selectedTradeWallet,
      setApprovingAssetLabel,
      setIsApprovingAllowance,
      setIsSubmitted,
      setShowModal,
      setCreateOrderApiError,
      setTradeAmountResetKey,
    ],
  );

  const handleAllowanceAndConfirm = useCallback(
    async (values, { openConfirmationModal }) => {
      openConfirmationModal(values);
    },
    [],
  );

  /** After modal Confirm — BitGo sends create_order; MetaMask runs approve/sign then create_order. */
  const handleCloseModal = useCallback(
    async (data) => {
      if (!data.status) {
        setCreateOrderApiError('');
        setShowModal(false);
        return;
      }

      if (allowanceInFlightRef.current) return;
      allowanceInFlightRef.current = true;
      try {
        await placeOrder(data.data);
      } finally {
        allowanceInFlightRef.current = false;
      }
    },
    [allowanceInFlightRef, placeOrder, setCreateOrderApiError, setShowModal],
  );

  return {
    handleAllowanceAndConfirm,
    handleCloseModal,
  };
}

export default usePlaceOrderSubmission;
